import React, { useState, useEffect, useCallback } from 'react';
import {
  Check,
  Star,
  Zap,
  Clock,
  Landmark,
  Loader2,
  ShieldCheck,
  CalendarDays,
  XCircle,
  AlertCircle,
  RefreshCw,
  Lock
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../../../api/axios';
import { useCurrency } from '../../../../hooks/useCurrency';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { applyWalletSpend } from '../../../../store/slices/authSlice';

// Matches PremiumStatusResponseDTO.kt
interface PremiumStatus {
  hasPremium: boolean;
  hasLifetime: boolean;
  expiresAt: string | null;
  premiumType: string | null;
  subscriptionType: string | null;
  autoRenewal: boolean;
}

interface PremiumPrice {
  subscriptionType: string;
  priceMinor: number;
}

const Premium = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { currencySymbol, formatAmountWithoutCurrency } = useCurrency();

  const [status, setStatus] = useState<PremiumStatus | null>(null);
  const [prices, setPrices] = useState<PremiumPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [wantAutoRenew, setWantAutoRenew] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string>('THREE_MONTHS');

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, pricesRes] = await Promise.all([
        api.get<PremiumStatus>('/user/premium/status'),
        api.get<PremiumPrice[]>('/premium/prices')
      ]);
      setStatus(statusRes.data);
      setPrices(pricesRes.data);

      if (pricesRes.data.length > 0 && !selectedPlan) {
        setSelectedPlan(pricesRes.data[0].subscriptionType);
      }
    } catch (err) {
      console.error('Failed to fetch premium data', err);
    } finally {
      setLoading(false);
    }
  }, [selectedPlan]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubscribe = async () => {
    if (status?.hasPremium) return;

    const planPrice = prices.find(p => p.subscriptionType === selectedPlan);
    if (!planPrice) {
      toast.error('Selected plan price not found.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/user/premium/subscribe', {
        subscriptionType: selectedPlan,
        autoRenewal: selectedPlan === 'LIFETIME' ? false : wantAutoRenew
      });

      // Update local wallet state using backend logic (locked-first spend)
      dispatch(applyWalletSpend(planPrice.priceMinor));

      toast.success('Subscription activated!');
      await fetchData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Transaction failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAutoRenewal = async () => {
    setSubmitting(true);
    const isCancelling = status?.autoRenewal;
    const endpoint = isCancelling
        ? '/user/premium/cancel-auto-renewal'
        : '/user/premium/activate-auto-renewal';

    try {
      await api.post(endpoint);
      toast.info(isCancelling ? 'Auto-renewal deactivated.' : 'Auto-renewal activated.');
      await fetchData();
    } catch (err: any) {
      toast.error('Update failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const planUIConfig: Record<string, { name: string; icon: any; highlight?: boolean }> = {
    THREE_MONTHS: { name: '3 Months', icon: <Clock className="text-blue-400" /> },
    SIX_MONTHS: { name: '6 Months', icon: <CalendarDays className="text-indigo-400" /> },
    TWELVE_MONTHS: { name: '12 Months', icon: <Star className="text-purple-400" /> },
    LIFETIME: { name: 'Lifetime', icon: <Zap className="text-yellow-400" />, highlight: true }
  };

  const benefits = [
    "Lower Sales Fees to 1.5%",
    "Reduced StripeOnboarding Fees to 0.5%",
    "Free Instant StripeOnboarding Requests",
    "Exclusive Profile Badge"
  ];

  if (loading) {
    return (
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
        </div>
    );
  }

  return (
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        <div className="bg-neutral-900/40 backdrop-blur-md rounded-lg p-6 shadow-sm border border-neutral-800 mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${status?.hasPremium ? 'bg-yellow-500/10 text-yellow-500' : 'bg-neutral-800 text-neutral-500'}`}>
                <Star className="w-6 h-6" fill={status?.hasPremium ? 'currentColor' : 'none'} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white uppercase">
                  {status?.hasPremium ? `Premium: ${status.subscriptionType?.replace('_', ' ')}` : 'Free Member'}
                </h2>
                <p className="text-sm text-neutral-400">
                  {status?.hasPremium && status.expiresAt
                      ? `Valid until: ${new Date(status.expiresAt).toLocaleDateString()}`
                      : status?.hasLifetime ? 'Lifetime Access Active' : 'Upgrade to reduce fees and support the platform'}
                </p>
              </div>
            </div>

            {status?.hasPremium && !status.hasLifetime && (
                <button
                    onClick={toggleAutoRenewal}
                    disabled={submitting}
                    className={`flex items-center gap-2 text-xs font-bold px-4 py-2.5 rounded-lg border transition-all ${
                        status.autoRenewal
                            ? 'text-red-400 bg-red-400/10 border-red-400/20 hover:bg-red-400/20'
                            : 'text-green-400 bg-green-400/10 border-green-400/20 hover:bg-green-400/20'
                    }`}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (status.autoRenewal ? <XCircle className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />)}
                  {status.autoRenewal ? 'STOP AUTO-RENEWAL' : 'START AUTO-RENEWAL'}
                </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {prices.map((plan) => {
            const config = planUIConfig[plan.subscriptionType] || { name: plan.subscriptionType, icon: <Star /> };
            const isSelected = selectedPlan === plan.subscriptionType;

            return (
                <div
                    key={plan.subscriptionType}
                    onClick={() => !status?.hasPremium && setSelectedPlan(plan.subscriptionType)}
                    className={`relative rounded-lg p-5 border-2 transition-all flex flex-col justify-between bg-neutral-900/40 backdrop-blur-sm ${
                        status?.hasPremium ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-neutral-700'
                    } ${isSelected && !status?.hasPremium ? 'border-blue-500 shadow-[0_0_20px_rgba(59,130,246,0.2)]' : 'border-neutral-800'}`}
                >
                  {config.highlight && (
                      <div className="lg:absolute lg:-top-3 lg:left-1/2 lg:-translate-x-1/2 bg-yellow-500 text-black text-[9px] px-2 py-0.5 lg:px-3 lg:py-1 rounded-full font-black tracking-widest shadow-lg z-10 uppercase mb-2 lg:mb-0 w-fit">
                        BEST VALUE
                      </div>
                  )}

                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-xs font-black uppercase tracking-widest text-neutral-500">{config.name}</span>
                      {config.icon}
                    </div>
                    <div className="mb-4">
                  <span className="text-3xl font-black text-white font-mono">
                    {currencySymbol}{formatAmountWithoutCurrency(plan.priceMinor / 100)}
                  </span>
                    </div>

                    <ul className="space-y-2">
                      {benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-[11px] text-neutral-400 leading-tight">
                            <Check className="w-3 h-3 text-green-500 mt-0.5 shrink-0" /> {benefit}
                          </li>
                      ))}
                    </ul>
                  </div>
                </div>
            );
          })}
        </div>

        <div className="bg-neutral-900/60 rounded-lg p-6 border border-neutral-800 shadow-xl">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3 bg-blue-500/10 px-4 py-2 rounded-lg border border-blue-500/20">
                <AlertCircle className="w-5 h-5 text-blue-400 shrink-0" />
                <span className="text-xs font-bold text-blue-100 uppercase tracking-tight">
                {status?.hasPremium
                    ? "You already have an active subscription. Manage renewal above."
                    : "Payment will be deducted from your internal Wallet balance."}
              </span>
              </div>

              {!status?.hasPremium && selectedPlan !== 'LIFETIME' && (
                  <div className="flex items-center gap-3 mt-2">
                    <button
                        onClick={() => setWantAutoRenew(!wantAutoRenew)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${wantAutoRenew ? 'bg-blue-600' : 'bg-neutral-700'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${wantAutoRenew ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <span className="text-sm font-bold text-neutral-300 italic">Auto-Renewal {wantAutoRenew ? 'active' : 'off'}</span>
                  </div>
              )}
            </div>

            <button
                onClick={handleSubscribe}
                disabled={submitting || status?.hasPremium}
                className={`w-full lg:w-auto px-12 py-4 rounded-lg font-black text-white transition-all flex items-center justify-center gap-3 shadow-[0_10px_30px_rgba(0,0,0,0.3)] ${
                    status?.hasPremium
                        ? 'bg-neutral-800 text-neutral-500 cursor-not-allowed border border-neutral-700'
                        : selectedPlan === 'LIFETIME'
                            ? 'bg-gradient-to-r from-yellow-600 to-orange-600 hover:brightness-110 active:scale-95'
                            : 'bg-blue-600 hover:bg-blue-700 active:scale-95'
                } uppercase tracking-widest`}
            >
              {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
              ) : status?.hasPremium ? (
                  <>
                    <Lock className="w-5 h-5" />
                    <span>Already Premium</span>
                  </>
              ) : (
                  <>
                    <Landmark className="w-5 h-5" />
                    <span>Confirm Purchase</span>
                  </>
              )}
            </button>
          </div>
        </div>
      </div>
  );
};

export default Premium;